import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Link, useParams } from 'react-router-dom';

import { API_KEY, pingUrl } from '@/account/api-keys';
import { errorCode } from '@/account/arcade-api';
import { ApiKeyDetail } from '@/components/ApiKeyDetail/ApiKeyDetail';
import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Spinner } from '@/components/Spinner/Spinner';

import { useApiKeyActions } from './useApiKeyActions';

const PAGE = 25;

/** One key's page: its facts, regenerate and revoke, and its request history. */
export function ApiKeyContainer() {
  const { id = '' } = useParams();
  const actions = useApiKeyActions();
  const { data, error, networkStatus, refetch, fetchMore } = useQuery(API_KEY, {
    variables: { id, first: PAGE },
    notifyOnNetworkStatusChange: true,
  });
  const back = <Link to="/settings/api-keys">← All API Keys</Link>;

  if (!data && networkStatus === NetworkStatus.loading) {
    return <Spinner />;
  }

  if (!data) {
    return (
      <Callout
        tone="danger"
        title={
          errorCode(error) === 'API_KEY_NOT_FOUND'
            ? 'This API Key does not exist'
            : 'Could not load this API Key'
        }
      >
        {back}
        {errorCode(error) === 'API_KEY_NOT_FOUND' ? null : (
          <Button variant="ghost" onClick={() => void refetch()}>
            Try again
          </Button>
        )}
      </Callout>
    );
  }

  const { requests, ...apiKey } = data.apiKey;

  return (
    <>
      <ApiKeyDetail
        apiKey={apiKey}
        requests={requests.edges.map((edge) => edge.node)}
        hasMore={requests.pageInfo.hasNextPage}
        loadingMore={networkStatus === NetworkStatus.fetchMore}
        pingUrl={pingUrl()}
        onLoadMore={() => void fetchMore({ variables: { after: requests.pageInfo.endCursor } })}
        onRefresh={() => void refetch({ after: null })}
        onRegenerate={() => actions.openRegenerate(apiKey)}
        onRevoke={() => actions.openRevoke(apiKey)}
        back={back}
      />
      {actions.dialog}
    </>
  );
}
